/* eslint-disable @typescript-eslint/no-floating-promises */
import { toast } from 'react-toastify'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import { getErrorMessage } from '../../helpers'
import { BaseApiResponse, QueryProps } from '../shared/shared.type'
import { apiSlice } from '../api/apiSlice'
import { Permission } from '../auth/auth.type'
import { type ErrorType } from '../shared/shared.type'
import {
  AddWalletAmountResponse,
  type AdminResponse,
  type CreatePermissionResponse,
  type CreateTeamMemberResponse,
  type CurrentUserResponse,
  type PermissionsPayload,
  type Role,
  type RolesPayload,
  type TeamMemberPayload,
  type UpdateTeamMemberPayload,
  type UpdateUserPayload,
  type UserPayload,
  WalletAmountsResponse,
} from './user.type'
import {
  AddAvailableLocationsPayload,
  AddAvailableLocationsSuccess,
  AvailableLocationsQuery,
  AvailableLocationsResponse,
  CreateDeliveryRegionPayload,
  DeliveryRegionImportPayload,
  DeliveryRegionImportPreview,
  DeliveryRegionImportPreviewQuery,
  DeliveryRegionsQuery,
  DeliveryRegionsResponse,
  PickUpLocation,
  PickUpLocationPayload,
  PickUpLocationsResponse,
  UpdateAvailableLocationsPayload,
  UpdateAvailableLocationsSuccess,
  UpdateDeliveryRegionPayload,
} from '../locations/locations.type'
import {
  CustomerHealthMetricsQuery,
  CustomerHealthMetricsResponse,
  CustomersQuery,
  CustomersResponse,
  SingleCustomerResponse,
} from '../customers/customers.type'

type UserResponse = {
  data: CurrentUserResponse[]
}

export const userApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getAllUsers: builder.query<UserResponse, QueryProps>({
      query: (args) => ({
        url: `v1/users`,
        method: 'GET',
        params: {
          ...args,
        },
      }),
      providesTags: [{ type: 'USER', id: 'LIST' }],
    }),
    getUser: builder.query<CurrentUserResponse, { id: number }>({
      query: ({ id }) => ({
        url: `v1/users/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, arg) => [{ type: 'USER', id: arg.id }],
      transformResponse: (response: { data: CurrentUserResponse }) =>
        response.data,
    }),
    createUser: builder.mutation<CurrentUserResponse, UserPayload>({
      query: (payload) => ({
        url: `v1/users`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'USER', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User added successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateUser: builder.mutation<
      CurrentUserResponse,
      { body: UpdateUserPayload; id: number }
    >({
      query: (payload) => ({
        url: `v1/users/${payload.id}`,
        method: 'PUT',
        body: payload.body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'USER', id: arg.id },
        { type: 'USER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    activateUser: builder.mutation<null, { id: number; active: boolean }>({
      query: ({ id, active }) => ({
        url: `v1/users/activate/${id}`,
        method: 'PUT',
        body: { active },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'USER', id: arg.id },
        { type: 'USER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deleteUser: builder.mutation<null, number>({
      query: (id) => ({
        url: `v1/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'USER', id: arg },
        { type: 'USER', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('User deleted successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getTeamMembers: builder.query<AdminResponse, QueryProps>({
      query: (query) => ({
        url: `v1/admin`,
        method: 'GET',
        params: { ...query },
      }),
      providesTags: [{ type: 'USER', id: 'LIST' }],
    }),
    inviteTeamMember: builder.mutation<
      CreateTeamMemberResponse,
      TeamMemberPayload
    >({
      query: (payload) => ({
        url: `v1/admin/invite-admin`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'USER', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Team member invited successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    editTeamMember: builder.mutation<
      CreateTeamMemberResponse,
      { payload: UpdateTeamMemberPayload; id: string }
    >({
      query: ({ payload, id }) => ({
        url: `v1/admin/edit/${id}`,
        method: 'PATCH',
        body: { ...payload },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'USER', id: arg.id },
        { type: 'USER', id: 'LIST' },
      ],
      transformResponse: (response: CreateTeamMemberResponse) => response,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Team member updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    deactivateActivateTeamMember: builder.mutation<
      CreateTeamMemberResponse,
      { payload: Partial<UpdateTeamMemberPayload>; id: string }
    >({
      query: ({ payload, id }) => ({
        url: `v1/admin/updateadmin/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'USER', id: arg.id },
        { type: 'USER', id: 'LIST' },
      ],
      transformResponse: (response: CreateTeamMemberResponse) => response,
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success(
            `Team member ${
              arg.payload.deleted ? 'deactivated' : 'activated'
            } successfully.`,
          )
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getRoles: builder.query<BaseApiResponse & { data: Role[] }, QueryProps>({
      query: (args) => ({
        url: `v1/role`,
        method: 'GET',
        params: {
          ...args,
        },
      }),
      providesTags: [{ type: 'ROLE', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createRole: builder.mutation<Role, RolesPayload>({
      query: (payload) => ({
        url: `v1/role`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'ROLE', id: 'LIST' }],
      transformResponse: (response: { data: Role }) => response.data,
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Role created successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    editRole: builder.mutation<Role, { payload: RolesPayload; id: string }>({
      query: ({ payload, id }) => ({
        url: `v1/role/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ROLE', id: arg.id },
        { type: 'ROLE', id: 'LIST' },
      ],
      transformResponse: (response: { data: Role }) => response.data,
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Role updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getPermissions: builder.query<
      BaseApiResponse & { data: Permission[] },
      QueryProps
    >({
      query: (args) => ({
        url: `v1/permission`,
        method: 'GET',
        params: {
          ...args,
        },
      }),
      providesTags: [{ type: 'PERMISSION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    createPermission: builder.mutation<
      CreatePermissionResponse,
      PermissionsPayload
    >({
      query: (payload) => ({
        url: `v1/permission`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'PERMISSION', id: 'LIST' }],
      transformResponse: (response: { data: CreatePermissionResponse }) =>
        response.data,
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled

          toast.success('Permission created successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    editPermission: builder.mutation<
      CreatePermissionResponse,
      { payload: PermissionsPayload; id: string }
    >({
      query: ({ payload, id }) => ({
        url: `v1/permission/${id}`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PERMISSION', id: arg.id },
        { type: 'PERMISSION', id: 'LIST' },
      ],
      transformResponse: (response: { data: CreatePermissionResponse }) =>
        response.data,
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
          toast.success('Permission updated successfully.')
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addAvailableLocations: builder.mutation<
      AddAvailableLocationsSuccess,
      AddAvailableLocationsPayload
    >({
      query: (payload) => ({
        url: `v1/system-settings/available-locations`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'LOCATION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const response = await queryFulfilled
          toast.success(response.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateAvailableLocations: builder.mutation<
      UpdateAvailableLocationsSuccess,
      UpdateAvailableLocationsPayload
    >({
      query: (payload) => ({
        url: `v1/system-settings/available-locations`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'LOCATION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const response = await queryFulfilled
          toast.success(response.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    fetchAvailableLocations: builder.query<
      AvailableLocationsResponse,
      AvailableLocationsQuery
    >({
      query: (payload) => ({
        url: `v1/system-settings/available-locations`,
        method: 'GET',
        params: {
          ...payload,
        },
      }),
      providesTags: [{ type: 'LOCATION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    toggleAvailableLocation: builder.mutation<
      { message: string },
      { id: string; isAvailable: boolean }
    >({
      query: (payload) => ({
        url: `v1/system-settings/available-locations/${payload.id}`,
        method: 'PATCH',
        body: {
          isAvailable: payload.isAvailable,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'LOCATION', id: arg.id },
        { type: 'LOCATION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    fetchPickupLocations: builder.query<PickUpLocationsResponse, QueryProps>({
      query: (payload) => ({
        url: `v1/system-settings/available-pickup-locations`,
        method: 'GET',
        params: {
          page: payload.page ?? 1,
          limit: payload.limit ?? NUMBER_OF_ITEMS_PER_PAGE,
        },
      }),
      providesTags: [{ type: 'PICKUP_LOCATION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    togglePickupLocation: builder.mutation<
      { message: string },
      { id: string; isAvailable: boolean }
    >({
      query: (payload) => ({
        url: `v1/system-settings/available-pickup-locations/${payload.id}`,
        method: 'PATCH',
        body: {
          isAvailable: payload.isAvailable,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PICKUP_LOCATION', id: arg.id },
        { type: 'PICKUP_LOCATION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addPickupLocation: builder.mutation<
      { success: boolean; message: string; data: PickUpLocation },
      PickUpLocationPayload
    >({
      query: (payload) => ({
        url: `v1/system-settings/available-pickup-locations`,
        method: 'PUT',
        body: payload,
      }),
      invalidatesTags: [{ type: 'PICKUP_LOCATION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    fetchDeliveryRegions: builder.query<
      DeliveryRegionsResponse,
      DeliveryRegionsQuery
    >({
      query: (payload) => ({
        url: `v1/system-settings/delivery-regions`,
        method: 'GET',
        params: {
          ...payload,
        },
      }),
      providesTags: [{ type: 'DELIVERY_REGION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    previewDeliveryRegionImport: builder.query<
      {
        success: boolean
        message: string
        data: DeliveryRegionImportPreview
      },
      DeliveryRegionImportPreviewQuery
    >({
      query: (payload) => ({
        url: `v1/system-settings/delivery-regions/import-preview`,
        method: 'GET',
        params: payload,
      }),
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    importDeliveryRegions: builder.mutation<
      { success: boolean; message: string },
      DeliveryRegionImportPayload
    >({
      query: (payload) => ({
        url: `v1/system-settings/delivery-regions/import`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'DELIVERY_REGION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addDeliveryRegion: builder.mutation<
      { success: boolean; message: string },
      CreateDeliveryRegionPayload
    >({
      query: (payload) => ({
        url: `v1/system-settings/delivery-regions`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'DELIVERY_REGION', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    toggleDeliveryRegion: builder.mutation<
      { success: boolean; message: string },
      { id: string; isAvailable: boolean }
    >({
      query: (payload) => ({
        url: `v1/system-settings/delivery-regions/${payload.id}`,
        method: 'PATCH',
        body: {
          isAvailable: payload.isAvailable,
        },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DELIVERY_REGION', id: arg.id },
        { type: 'DELIVERY_REGION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    updateDeliveryRegion: builder.mutation<
      { success: boolean; message: string },
      { id: string; payload: UpdateDeliveryRegionPayload }
    >({
      query: ({ id, payload }) => ({
        url: `v1/system-settings/delivery-regions/${id}`,
        method: 'PATCH',
        body: payload,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'DELIVERY_REGION', id: arg.id },
        { type: 'DELIVERY_REGION', id: 'LIST' },
      ],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    fetchWalletAmounts: builder.query<WalletAmountsResponse, void>({
      query: () => ({
        url: `v1/admin/fixed-deposit-amount`,
        method: 'GET',
      }),
      providesTags: [{ type: 'WALLET', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    addWalletAmount: builder.mutation<
      AddWalletAmountResponse,
      { amount: string }
    >({
      query: (payload) => ({
        url: `v1/admin/fixed-deposit-amount`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: [{ type: 'WALLET', id: 'LIST' }],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getAllCustomers: builder.query<CustomersResponse, CustomersQuery>({
      query: (args) => ({
        url: `v1/customers`,
        method: 'GET',
        params: {
          ...args,
        },
      }),
      providesTags: [{ type: 'CUSTOMER', id: 'LIST' }],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getCustomerHealthMetrics: builder.query<
      CustomerHealthMetricsResponse,
      CustomerHealthMetricsQuery
    >({
      query: (args) => ({
        url: `v1/customers/health-metrics`,
        method: 'GET',
        params: args,
      }),
      providesTags: ['CUSTOMER'],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getSingleCustomers: builder.query<
      SingleCustomerResponse,
      { id: string; query: QueryProps }
    >({
      query: (args) => ({
        url: `v1/customers/${args.id}`,
        method: 'GET',
        params: {
          ...args.query,
        },
      }),
      providesTags: (_result, _error, arg) => [
        { type: 'CUSTOMER', id: arg.id },
      ],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
  }),
})

export const {
  useGetAllUsersQuery,
  useGetUserQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useUpdateUserMutation,
  useActivateUserMutation,
  useGetRolesQuery,
  useGetPermissionsQuery,
  useGetTeamMembersQuery,
  useInviteTeamMemberMutation,
  useCreatePermissionMutation,
  useCreateRoleMutation,
  useEditTeamMemberMutation,
  useEditRoleMutation,
  useEditPermissionMutation,
  useDeactivateActivateTeamMemberMutation,
  useFetchAvailableLocationsQuery,
  useToggleAvailableLocationMutation,
  useLazyFetchAvailableLocationsQuery,
  useFetchPickupLocationsQuery,
  useTogglePickupLocationMutation,
  useAddPickupLocationMutation,
  useFetchDeliveryRegionsQuery,
  useAddDeliveryRegionMutation,
  useLazyPreviewDeliveryRegionImportQuery,
  useImportDeliveryRegionsMutation,
  useToggleDeliveryRegionMutation,
  useUpdateDeliveryRegionMutation,
  useUpdateAvailableLocationsMutation,
  useAddAvailableLocationsMutation,
  useFetchWalletAmountsQuery,
  useAddWalletAmountMutation,
  useGetAllCustomersQuery,
  useGetCustomerHealthMetricsQuery,
  useGetSingleCustomersQuery,
} = userApiSlice
